import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePanelResize, useElementWidth } from '../components/PanelResizer';

const COMMENTS_DEFAULT_WIDTH = 320;
const COMMENTS_MIN_WIDTH = 220;
/**
 * What the comments panel has to leave for the document text. Below roughly
 * this the prose column stops being readable, so on a narrow editor pane the
 * panel collapses to its minimum rather than the text collapsing to nothing.
 */
const COMMENTS_MIN_TEXT_WIDTH = 320;

/**
 * Finds the editor pane inside `host`, and reports whether the comments panel
 * is currently docked open in it.
 *
 * The pane and the panel both belong to the shared runtime — the console never
 * renders either — so this reads the DOM the runtime produces instead of
 * threading state through the editor mount. `comments-panel-open` is the class
 * the runtime's `CommentPlugin` toggles on the pane for exactly this purpose:
 * reserving room beside itself.
 */
function useCommentsPanelPane(host: HTMLElement | null): { pane: HTMLElement | null; open: boolean } {
  const [pane, setPane] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!host) {
      setPane(null);
      return;
    }
    // The editor mounts asynchronously and remounts when the document changes,
    // so this watches rather than queries once. The `isConnected` short-circuit
    // is what keeps it off the hot path: every keystroke mutates this subtree,
    // and re-running the query each time would walk the whole document.
    let current: HTMLElement | null = null;
    const sync = () => {
      if (current?.isConnected) return;
      current = host.querySelector<HTMLElement>('.editor-container');
      setPane(current);
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [host]);

  useEffect(() => {
    if (!pane) {
      setOpen(false);
      return;
    }
    const sync = () => setOpen(pane.classList.contains('comments-panel-open'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(pane, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [pane]);

  return { pane, open };
}

/**
 * The document surface, with a draggable split between the text and the
 * runtime's docked comments panel.
 *
 * The width is published as `--nim-comments-panel-width` on this section and
 * inherits down to both halves of the runtime's split — the panel's own width
 * and the padding the editor scroller reserves for it — so the two can't drift
 * apart. Unset, which is every other host, they stay at the 320px default.
 */
export function DocumentWorkspace({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const { pane, open } = useCommentsPanelPane(host);
  // Clamped against the editor pane, not the window: the pane already shrank by
  // whatever the document tree took, and the text column lives in what's left.
  const paneWidth = useElementWidth(pane);
  const resize = usePanelResize({
    storageKey: 'document-comments',
    defaultWidth: COMMENTS_DEFAULT_WIDTH,
    edge: 'inline-start',
    label: 'Resize comments panel',
    containerWidth: paneWidth,
    min: COMMENTS_MIN_WIDTH,
    minComplement: COMMENTS_MIN_TEXT_WIDTH,
  });

  return (
    <section
      ref={setHost}
      // Column, so the editor grows into the height the content column gives it.
      className="document-workspace flex h-full min-h-0 flex-col"
      style={{ '--nim-comments-panel-width': `${resize.width}px` } as CSSProperties}
    >
      {children}
      {/* Portalled into the runtime's editor pane so the handle spans exactly
          the panel's height, and no more: the pane is the panel's own
          positioning context. */}
      {open && pane && createPortal(
        <div className="document-comments-resizer" {...resize.separatorProps} />,
        pane,
      )}
    </section>
  );
}
